import React, { useState } from 'react';
import classes from '../templates.module.scss';
import formClasses from '../../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import {
  ProtocolTemplate,
  ProtocolTemplatePayload,
  ProtocolTemplateSection,
} from '../../../services/templates.service';
import { slugifySectionKey, uniqueSectionKey } from '../utils/slugify-key';
import SectionsEditor, { createEmptySectionRow, SectionRow } from './sections-editor.component';

const cn = createCn('templates', classes);

function rowsFromTemplate(template: ProtocolTemplate | null): SectionRow[] {
  if (!template || template.sections.length === 0) {
    return [createEmptySectionRow()];
  }
  return template.sections.map((section, index) => ({
    id: `existing-${index}-${section.key}`,
    key: section.key,
    title: section.title,
    hint: section.hint || '',
    // Задача TPL1: старые секции без instruction/format читаются как есть — hint
    // остаётся в своём поле (fallback на бэке), формат по умолчанию "paragraph".
    instruction: section.instruction || '',
    format: section.format || 'paragraph',
    itemFormat: section.item_format || '',
  }));
}

/** Ключ для новых секций вычисляется из заголовка автоматически; у уже сохранённых секций ключ не меняется. */
function buildSectionsPayload(rows: SectionRow[]): ProtocolTemplateSection[] {
  const usedKeys = new Set<string>();
  rows.forEach((row) => {
    if (row.key) usedKeys.add(row.key);
  });

  return rows
    .filter((row) => row.title.trim())
    .map((row) => {
      const title = row.title.trim();
      const hint = row.hint.trim() || undefined;
      const instruction = row.instruction.trim() || undefined;
      const format = row.format || 'paragraph';
      const item_format = format === 'list' ? row.itemFormat.trim() || undefined : undefined;
      if (row.key) {
        return { key: row.key, title, hint, instruction, format, item_format };
      }
      const base = slugifySectionKey(title);
      const key = uniqueSectionKey(base, usedKeys);
      usedKeys.add(key);
      return { key, title, hint, instruction, format, item_format };
    });
}

interface TemplateFormProps {
  /** null — создание нового шаблона, иначе — редактирование переданного. */
  template: ProtocolTemplate | null;
  saving: boolean;
  onSubmit: (payload: ProtocolTemplatePayload) => void;
  onCancel: () => void;
}

const TemplateForm = ({ template, saving, onSubmit, onCancel }: TemplateFormProps) => {
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [rows, setRows] = useState<SectionRow[]>(() => rowsFromTemplate(template));
  const [validationError, setValidationError] = useState('');

  const isEditing = Boolean(template);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setValidationError('Укажите название шаблона');
      return;
    }

    const sections = buildSectionsPayload(rows);
    if (sections.length === 0) {
      setValidationError('Добавьте хотя бы одну секцию с заголовком');
      return;
    }

    setValidationError('');
    onSubmit({ name: name.trim(), description: description.trim(), sections });
  };

  return (
    <form className={cn('form')} onSubmit={handleSubmit}>
      <div className={cn('form-title')}>
        {isEditing ? `Редактирование шаблона «${template?.name}»` : 'Новый шаблон'}
      </div>

      <div className={formClasses['form-group']}>
        <label>Название</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="например, Протокол совещания КТЖ"
          disabled={saving}
          autoFocus
        />
      </div>

      <div className={formClasses['form-group']}>
        <label>Описание (необязательно)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Краткое описание, для чего этот шаблон"
          disabled={saving}
        />
      </div>

      <SectionsEditor rows={rows} onChange={setRows} disabled={saving} />

      {validationError && <div className={cn('form-error')}>{validationError}</div>}

      <div className={cn('form-actions')}>
        <button type="submit" className={cn('save-btn')} disabled={saving}>
          {saving ? 'Сохранение…' : isEditing ? 'Сохранить изменения' : 'Создать шаблон'}
        </button>
        <button type="button" className={cn('cancel-btn')} onClick={onCancel} disabled={saving}>
          Отмена
        </button>
      </div>
    </form>
  );
};

export default TemplateForm;
